import { z } from 'zod';
/**
 * Shared auth contract. The API validates incoming requests against these schemas
 * and the web app imports the inferred types, so a change to a payload shape is a
 * compile error on both sides rather than a runtime surprise.
 */
/**
 * Usernames are only ever shown to you and embedded in the passkey so your
 * password manager can label it. Kept deliberately narrow so it is safe to
 * render anywhere without escaping concerns.
 */
export const usernameSchema = z
    .string()
    .trim()
    .min(3, 'Username must be at least 3 characters.')
    .max(32, 'Username must be at most 32 characters.')
    .regex(/^[a-zA-Z0-9._-]+$/, 'Use only letters, numbers, dots, underscores or hyphens.');
/**
 * Tells the login screen which of its two modes to render.
 *
 * `canRegister` is false as soon as an account exists. Registration is a one-time
 * bootstrap, not an open endpoint — otherwise anyone who found the URL could
 * enrol their own passkey and get in.
 */
export const authStatusSchema = z.object({
    authenticated: z.boolean(),
    canRegister: z.boolean(),
    username: z.string().nullable(),
});
export const registerStartRequestSchema = z.object({
    username: usernameSchema,
});
/**
 * The WebAuthn ceremony payloads are passed through opaquely.
 *
 * We deliberately do NOT re-model `PublicKeyCredentialCreationOptionsJSON` or the
 * attestation/assertion responses here. @simplewebauthn already defines those types
 * precisely on both the browser and server side, and hand-mirroring a security
 * protocol's wire format is how subtle verification bugs get introduced.
 */
export const webauthnResponseSchema = z.record(z.string(), z.unknown());
export const sessionUserSchema = z.object({
    id: z.number().int().positive(),
    username: z.string(),
    displayName: z.string(),
    createdAt: z.string(),
});
export const verifyResultSchema = z.object({
    verified: z.boolean(),
    user: sessionUserSchema,
});
/** Shape of every error the API returns, so the client can render one code path. */
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
    }),
});
/** Error codes the web app branches on. Anything else is shown verbatim. */
export const AUTH_ERROR = {
    REGISTRATION_CLOSED: 'REGISTRATION_CLOSED',
    NO_CHALLENGE: 'NO_CHALLENGE',
    VERIFICATION_FAILED: 'VERIFICATION_FAILED',
    UNKNOWN_CREDENTIAL: 'UNKNOWN_CREDENTIAL',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    USERNAME_TAKEN: 'USERNAME_TAKEN',
};
//# sourceMappingURL=auth.js.map